import React from 'react';
import {
  ArrowUp,
  ArrowDown,
  CaretDown,
  CaretRight,
  Trash,
  Copy,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AnswerOptionsSection } from './AnswerOptionsSection';
import { ExtractionConfigSection } from './ExtractionConfigSection';
import { DocumentLinkSection } from './DocumentLinkSection';
import type { QuestionItem, AnswerOptionItem } from './types';
import { DueDiligenceAnswerType, ConditionGroup } from 'shared/types/due-diligence';
import type { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';

interface QuestionCardProps {
  question: QuestionItem;
  questionIndex: number;
  isExpanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  fileLabels: DatabaseFileLabel[];
  questions: QuestionItem[];
  conditionsClipboard: ConditionGroup | null;
  onToggleExpanded: () => void;
  onUpdate: (index: number, updates: Partial<QuestionItem>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
  onDuplicate: (index: number) => void;
  onAddAnswerOption: (questionIndex: number) => void;
  onUpdateAnswerOption: (
    questionIndex: number,
    optionIndex: number,
    updates: Partial<AnswerOptionItem>,
  ) => void;
  onRemoveAnswerOption: (questionIndex: number, optionIndex: number) => void;
  onCopyConditions: (questionIndex: number, optionIndex: number) => void;
  onPasteConditions: (questionIndex: number, optionIndex: number) => void;
  onOpenDocumentLabelPicker: (questionIndex: number) => void;
}

const ANSWER_TYPE_CONFIG = [
  {
    type: DueDiligenceAnswerType.OPTIONS,
    label: 'Status vraag',
    activeClass: 'bg-moss-primary text-white',
    title: "Vraag met status bolletje (groen/oranje/rood). Gebruik voor vragen als: 'Is het EPC geldig?'",
  },
  {
    type: DueDiligenceAnswerType.SIMPLE_SELECT,
    label: 'Selectie vraag',
    activeClass: 'bg-slate-600 text-white',
    title: 'Selectie zonder status bolletje. Gebruik voor hulpvragen of afhankelijkheden.',
  },
  {
    type: DueDiligenceAnswerType.DOCUMENT_EXISTS,
    label: 'Document aanwezig',
    activeClass: 'bg-sky-600 text-white',
    title: 'Controleert automatisch of een bestand met het gelinkte label bestaat.',
  },
  {
    type: DueDiligenceAnswerType.EXTRACTION,
    label: 'Extractie',
    activeClass: 'bg-violet-600 text-white',
    title: 'Extraheert een waarde uit documenten voor gebruik in opvolgende vragen.',
  },
];

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionIndex,
  isExpanded,
  isFirst,
  isLast,
  fileLabels,
  questions,
  conditionsClipboard,
  onToggleExpanded,
  onUpdate,
  onRemove,
  onMove,
  onDuplicate,
  onAddAnswerOption,
  onUpdateAnswerOption,
  onRemoveAnswerOption,
  onCopyConditions,
  onPasteConditions,
  onOpenDocumentLabelPicker,
}) => {
  const isExtractionType = question.answerType === DueDiligenceAnswerType.EXTRACTION;

  return (
    <Card className="border border-border overflow-hidden" data-tour="dd-question-card">
      {/* Header area */}
      <div className="bg-muted/30 p-4 space-y-3">
        {/* Top row: reorder, expand, question counter, actions */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => onMove(questionIndex, 'up')}
            disabled={isFirst}
            title="Omhoog verplaatsen"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => onMove(questionIndex, 'down')}
            disabled={isLast}
            title="Omlaag verplaatsen"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onToggleExpanded}
            title={isExpanded ? 'Inklappen' : 'Uitklappen'}
          >
            {isExpanded ? (
              <CaretDown className="h-4 w-4" />
            ) : (
              <CaretRight className="h-4 w-4" />
            )}
          </Button>

          <div className="flex-1 space-y-3">
            {/* Question name and help text */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs mb-1.5 block">Vraag</Label>
                <Input
                  placeholder="bijv. Is de rookmelder aanwezig?"
                  value={question.displayName}
                  onChange={(e) =>
                    onUpdate(questionIndex, {
                      displayName: e.target.value,
                      fieldName: e.target.value
                        .toLowerCase()
                        .replace(/\s+/g, '_')
                        .replace(/[^a-z0-9_]/g, ''),
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Hulptekst (optioneel)</Label>
                <Input
                  placeholder="Extra uitleg bij deze vraag"
                  value={question.helpText || ''}
                  onChange={(e) => onUpdate(questionIndex, { helpText: e.target.value })}
                />
              </div>
            </div>

            {/* Answer type selector + badges */}
            <div className="flex items-center gap-2" data-tour="dd-answer-type-selector">
              <Label className="text-xs shrink-0">Type:</Label>
              <div className="flex gap-1.5">
                {ANSWER_TYPE_CONFIG.map((config) => (
                  <button
                    key={config.type}
                    type="button"
                    onClick={() => onUpdate(questionIndex, { answerType: config.type })}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                      question.answerType === config.type
                        ? config.activeClass
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    title={config.title}
                  >
                    {config.label}
                  </button>
                ))}
              </div>

              <div className="flex-1" />
              <div className="flex items-center gap-2">
                {!isExtractionType && (
                  <Badge variant="secondary" className="text-xs">
                    {question.answerOptions.length} optie(s)
                  </Badge>
                )}
                {isExtractionType && (
                  <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">
                    Extractie
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5 self-start">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => onDuplicate(questionIndex)}
              title="Dupliceren"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => onRemove(questionIndex)}
              title="Verwijderen"
            >
              <Trash className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded content - sections render their own CardContent */}
      {isExpanded && (
        <>
          {isExtractionType ? (
            <ExtractionConfigSection
              question={question}
              questionIndex={questionIndex}
              onUpdate={onUpdate}
            />
          ) : (
            <AnswerOptionsSection
              question={question}
              questionIndex={questionIndex}
              questions={questions}
              fileLabels={fileLabels}
              conditionsClipboard={conditionsClipboard}
              onAddOption={onAddAnswerOption}
              onUpdateOption={onUpdateAnswerOption}
              onRemoveOption={onRemoveAnswerOption}
              onCopyConditions={onCopyConditions}
              onPasteConditions={onPasteConditions}
            />
          )}

          <DocumentLinkSection
            question={question}
            questionIndex={questionIndex}
            fileLabels={fileLabels}
            onUpdate={onUpdate}
            onOpenDocumentLabelPicker={onOpenDocumentLabelPicker}
          />
        </>
      )}
    </Card>
  );
};
